import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import DiamondIcon from "../assets/images/diamond.png";
import {
  getWeeklyStarSettings,
  removeWeeklyStarGift,
  updateWeeklyStarGifts,
  updateWeeklyStarRewards,
} from "../store/weeklyStar/action";
import WeeklyStarRewardDialog from "../component/dialog/WeeklyStarRewardDialog";
import WeeklyStarGiftDialog from "../component/dialog/WeeklyStarGiftDialog";
import {
  OPEN_WEEKLY_STAR_GIFT_DIALOG,
  OPEN_WEEKLY_STAR_REWARD_DIALOG,
} from "../store/weeklyStar/types";
import { warning } from "../util/Alert";

const MAX_GIFTS = 3;
const MAX_REWARDS_PER_RANK = 3;

const rewardLabel = (reward) =>
  reward?.type === "diamond" ? "Diamonds" : reward?.item?.name;

const rewardImage = (reward) =>
  reward?.type === "diamond"
    ? DiamondIcon
    : reward?.item?.imagePreview || reward?.item?.image;

const rewardAmount = (reward) => {
  if (reward?.type === "diamond") return parseInt(reward?.diamond).toLocaleString();
  if (reward?.type === "gift") return `x${reward?.giftQuantity}`;
  return `${reward?.validity} ${reward?.validityType}`;
};

const SlotActions = ({ onEdit, onUnset, editTitle, unsetTitle }) => (
  <div className="d-flex flex-column" style={{ marginRight: -35 }}>
    <button
      type="button"
      title={editTitle}
      onClick={onEdit}
      className="fas fa-edit text-white p-2 bg-primary rounded-circle"
      style={{ fontSize: 15 }}
    />
    <button
      type="button"
      title={unsetTitle}
      onClick={onUnset}
      className="fas fa-times text-white p-2 bg-danger rounded-circle mt-2"
      style={{ fontSize: 15 }}
    />
  </div>
);

const AddSlot = ({ label, height, onClick }) => (
  <div className="col">
    <button
      type="button"
      onClick={onClick}
      className="btn btn-outline-primary d-flex flex-column align-items-center justify-content-center mx-2 my-4 w-100"
      style={{ height }}
    >
      <i className="fas fa-plus" style={{ fontSize: 20 }} />
      <span className="mt-2">{label}</span>
    </button>
  </div>
);

const RankRewards = ({ title, rank, rewards, onEdit, onUnset, onAdd }) => (
  <>
    <h3 className="text-white mt-5">{title}</h3>
    <div className="mb-2 d-flex flex-row align-items-center">
      {rewards.map((reward, index) => (
        <div className="col" key={reward._id || index}>
          <div className="d-flex flex-column align-items-start mx-2 my-4">
            <div className="d-flex flex-row align-items-end">
              <img
                style={{ height: 100 }}
                src={rewardImage(reward)}
                alt={rewardLabel(reward)}
              />
              <SlotActions
                editTitle="Edit reward"
                unsetTitle="Unset reward"
                onEdit={() => onEdit(reward, index, rank)}
                onUnset={() => onUnset(rank, index)}
              />
            </div>
            <span>{rewardLabel(reward)}</span>
            <span>{rewardAmount(reward)}</span>
          </div>
        </div>
      ))}

      {rewards.length < MAX_REWARDS_PER_RANK && (
        <AddSlot
          label="Set Reward"
          height={140}
          onClick={() => onAdd(rank, rewards.length)}
        />
      )}

      {rewards.length === 0 && (
        <div className="col">
          <span className="text-muted">No reward set for this rank.</span>
        </div>
      )}
    </div>
  </>
);

const WeeklyStar = (props) => {
  const dispatch = useDispatch();
  const { settings } = useSelector((state) => state.weeklyStar);

  useEffect(() => {
    props.getWeeklyStarSettings();
  }, []);

  const gifts = settings?.gifts || [];
  const rewards = settings?.rewards || {};

  const handleEditReward = (data, index, rank) => {
    dispatch({
      type: OPEN_WEEKLY_STAR_REWARD_DIALOG,
      payload: { rewardData: data, index, rank },
    });
  };

  const handleAddReward = (rank, index) => {
    dispatch({
      type: OPEN_WEEKLY_STAR_REWARD_DIALOG,
      payload: { rewardData: null, index, rank },
    });
  };

  const handleUnsetReward = (rank, index) => {
    warning()
      .then((isConfirmed) => {
        if (!isConfirmed) return;
        props.updateWeeklyStarRewards({
          ...rewards,
          [rank]: (rewards[rank] || []).filter((_, i) => i !== index),
        });
      })
      .catch((err) => console.log(err));
  };

  const handleEditGift = (data, index) => {
    dispatch({
      type: OPEN_WEEKLY_STAR_GIFT_DIALOG,
      payload: { giftData: data, index },
    });
  };

  const handleAddGift = () => {
    dispatch({
      type: OPEN_WEEKLY_STAR_GIFT_DIALOG,
      payload: { giftData: null, index: null },
    });
  };

  const handleUnsetGift = (index) => {
    warning()
      .then((isConfirmed) => {
        if (isConfirmed) props.removeWeeklyStarGift(index);
      })
      .catch((err) => console.log(err));
  };

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-3 text-white">Weekly Vibe Star</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav
              aria-label="breadcrumb"
              className="breadcrumb-header float-start float-lg-end"
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Weekly Vibe Star
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
      <div>
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Active Gifts</h4>
              </div>
              <div className="card-body">
                <div className="mb-2 d-flex flex-row align-items-center">
                  {gifts.map((gift, index) => (
                    <div className="col" key={gift._id}>
                      <div className="d-flex flex-column align-items-start mx-2 my-4">
                        <div className="d-flex flex-row align-items-end">
                          <img
                            style={{ height: 200 }}
                            src={gift?.imagePreview || gift?.image}
                            alt={gift?.name}
                          />
                          <SlotActions
                            editTitle="Change gift"
                            unsetTitle="Unset gift"
                            onEdit={() => handleEditGift(gift, index)}
                            onUnset={() => handleUnsetGift(index)}
                          />
                        </div>
                        <span>{gift?.name}</span>
                      </div>
                    </div>
                  ))}

                  {gifts.length < MAX_GIFTS && (
                    <AddSlot
                      label="Set Eligible Gift"
                      height={200}
                      onClick={handleAddGift}
                    />
                  )}

                  {gifts.length === 0 && (
                    <div className="col">
                      <span className="text-muted">
                        No eligible gift set. The event will not record any gift
                        until one is set.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Rewards</h4>
              </div>
              <div className="card-body">
                <RankRewards
                  title="Top 1 Rewards"
                  rank="first"
                  rewards={rewards.first || []}
                  onEdit={handleEditReward}
                  onUnset={handleUnsetReward}
                  onAdd={handleAddReward}
                />
                <RankRewards
                  title="Top 2 Rewards"
                  rank="second"
                  rewards={rewards.second || []}
                  onEdit={handleEditReward}
                  onUnset={handleUnsetReward}
                  onAdd={handleAddReward}
                />
                <RankRewards
                  title="Top 3 Rewards"
                  rank="third"
                  rewards={rewards.third || []}
                  onEdit={handleEditReward}
                  onUnset={handleUnsetReward}
                  onAdd={handleAddReward}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <WeeklyStarRewardDialog />
      <WeeklyStarGiftDialog />
    </>
  );
};

export default connect(null, {
  getWeeklyStarSettings,
  updateWeeklyStarGifts,
  updateWeeklyStarRewards,
  removeWeeklyStarGift,
})(WeeklyStar);
